// One canonical time formatter for the whole app. Always 24-hour because
// AM/PM only makes sense for a narrow set of locales and we want EPG times
// to be unambiguous.
//
// `timezone`:
//   null/undefined  -> use the browser's local timezone (the "Auto" choice
//                       in the picker; correct for users whose system clock
//                       already matches the EPG region)
//   IANA name       -> render the timestamp in this timezone, e.g.
//                       'Europe/Budapest'. Honors DST automatically, unlike
//                       a fixed UTC offset.

export function formatTime(iso, timezone) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const options = { hour: '2-digit', minute: '2-digit', hour12: false };
  if (timezone) options.timeZone = timezone;
  return new Intl.DateTimeFormat([], options).format(d);
}

// Pretty label for the picker, e.g. "Europe/Budapest (UTC+02:00)".
export function formatTimezoneLabel(timezone) {
  if (!timezone) {
    return `Auto (${browserTimezone()})`;
  }
  const offset = currentOffsetLabel(timezone);
  return offset ? `${timezone} (${offset})` : timezone;
}

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Best-effort current UTC offset for a zone, used purely for display.
// Returns "UTC+02:00" / "UTC-05:30" / "UTC". Empty string if the zone is
// not resolvable in this runtime.
export function currentOffsetLabel(timezone) {
  if (!timezone) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    if (!raw) return '';
    if (raw === 'GMT' || raw === 'UTC') return 'UTC';
    const m = raw.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/);
    if (!m) return raw;
    const sign = m[1];
    const hh = String(Number(m[2])).padStart(2, '0');
    const mm = (m[3] || '00').padStart(2, '0');
    return `UTC${sign}${hh}:${mm}`;
  } catch {
    return '';
  }
}

// Two-letter ISO country code -> a single representative IANA timezone.
// EPG schedules are produced at country granularity, so picking the
// capital/main broadcast zone is correct in practice. Countries that span
// DST-misaligned zones (US, RU, BR, AU) are left out — the user can pick
// manually from TIMEZONE_OPTIONS.
export const COUNTRY_TIMEZONES = {
  HU: 'Europe/Budapest',
  AT: 'Europe/Vienna',
  DE: 'Europe/Berlin',
  CH: 'Europe/Zurich',
  CZ: 'Europe/Prague',
  SK: 'Europe/Bratislava',
  PL: 'Europe/Warsaw',
  RO: 'Europe/Bucharest',
  RS: 'Europe/Belgrade',
  HR: 'Europe/Zagreb',
  SI: 'Europe/Ljubljana',
  BA: 'Europe/Sarajevo',
  BG: 'Europe/Sofia',
  GR: 'Europe/Athens',
  IT: 'Europe/Rome',
  FR: 'Europe/Paris',
  ES: 'Europe/Madrid',
  PT: 'Europe/Lisbon',
  NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels',
  LU: 'Europe/Luxembourg',
  DK: 'Europe/Copenhagen',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  FI: 'Europe/Helsinki',
  IE: 'Europe/Dublin',
  GB: 'Europe/London',
  UK: 'Europe/London',
  TR: 'Europe/Istanbul',
  UA: 'Europe/Kyiv',
  MD: 'Europe/Chisinau',
  BY: 'Europe/Minsk',
  LT: 'Europe/Vilnius',
  LV: 'Europe/Riga',
  EE: 'Europe/Tallinn',
};

export function timezoneForCountry(country) {
  if (!country) return '';
  return COUNTRY_TIMEZONES[String(country).trim().toUpperCase().slice(0, 2)] || '';
}

// The set of timezones we expose in the picker. We only list zones that
// are likely to ship with broadcast EPG; anyone needing something exotic
// can set vibe-prefs.timezone in localStorage directly.
export const TIMEZONE_OPTIONS = [
  'UTC',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/Vienna',
  'Europe/Rome',
  'Europe/Prague',
  'Europe/Bratislava',
  'Europe/Budapest',
  'Europe/Warsaw',
  'Europe/Belgrade',
  'Europe/Zagreb',
  'Europe/Ljubljana',
  'Europe/Sarajevo',
  'Europe/Bucharest',
  'Europe/Sofia',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Kyiv',
  'Europe/Chisinau',
  'Europe/Vilnius',
  'Europe/Riga',
  'Europe/Tallinn',
  'Europe/Helsinki',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
];
